import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  AccountBalanceWalletOutlined,
  DownloadOutlined,
  ReceiptLongOutlined,
  RefreshRounded,
  SavingsOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';
import { getFacilitiyV2, getPaymentSummary } from '../../../../helpers/api';
import { getYears } from '../../../../helpers/dateHelpers';
import LoaderSkeleton from '../../../common/LoaderSkeleton';

// Shared building blocks for the Reporting > Financial pages (PaymentReport, InvoiceReport)

export const BRAND = '#0c5bab';

export const naira = (value) =>
  value == null || Number.isNaN(Number(value))
    ? '–'
    : new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        maximumFractionDigits: 2,
      }).format(Number(value));

export const formatDate = (value) =>
  value && !String(value).startsWith('0001')
    ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '–';

export const titleCase = (value = '') =>
  String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const reportPeriod = (r) =>
  r.reportQuarter && r.reportYear ? `Q${r.reportQuarter} ${r.reportYear}` : r.reportTitle || '';

const statusColor = (status = '') => {
  const s = String(status).toUpperCase();
  if (['APPROVED', 'PAID', 'CLOSED', 'SUCCESS', 'SUCCESSFUL', 'VERIFIED'].includes(s)) return '#2e7d32';
  if (['AWAITING_APPROVAL', 'PENDING', 'ONGOING', 'PARTIAL', 'PARTIALLY_PAID'].includes(s)) return '#ed6c02';
  if (['REJECTED', 'DECLINED', 'FAILED', 'CANCELLED', 'OVERDUE'].includes(s)) return '#d32f2f';
  if (['OPEN', 'UNPAID'].includes(s)) return BRAND;
  return '#757575';
};

export function StatusChip({ status }) {
  if (!status) {
    return (
      <Typography variant="caption" color="text.disabled">
        –
      </Typography>
    );
  }
  const color = statusColor(status);
  return (
    <Chip
      size="small"
      label={titleCase(status)}
      sx={{ fontWeight: 600, bgcolor: alpha(color, 0.1), color, height: 22 }}
    />
  );
}

// makeApiCall rejects "no records" responses; treat those as an empty list
const isEmptyResult = (error) => {
  const status = error?.result?.status || error?.data?.result?.status;
  return status?.code === '03' || /empty|no record/i.test(status?.desc || '');
};

const downloadCsv = (fileName, columns, rows) => {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const valueOf = (c, r) => (c.csv ? c.csv(r) : typeof r[c.key] === 'object' ? '' : r[c.key]);
  const lines = [columns.map((c) => escape(c.label)).join(',')].concat(
    rows.map((r) => columns.map((c) => escape(valueOf(c, r))).join(','))
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

function SummaryTiles({ summary, loading }) {
  const total = Number(summary?.totalAmount) || 0;
  const paid = Number(summary?.totalAmountPaid) || 0;
  const rate = total > 0 ? Math.round((paid / total) * 100) : null;

  const tiles = [
    { label: 'Total invoiced', value: naira(summary?.totalAmount), icon: <ReceiptLongOutlined />, color: BRAND },
    { label: 'Total paid', value: naira(summary?.totalAmountPaid), icon: <SavingsOutlined />, color: '#2e7d32' },
    {
      label: 'Outstanding',
      value: naira(summary?.totalOutstandingAmount),
      icon: <AccountBalanceWalletOutlined />,
      color: '#d32f2f',
    },
    {
      label: 'Collection rate',
      value: rate == null ? '–' : `${rate}%`,
      icon: <TrendingUpOutlined />,
      color: '#6a3fb5',
      rate,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' },
        gap: 1.5,
        mb: 2.5,
      }}
    >
      {tiles.map((t) => (
        <Paper
          key={t.label}
          elevation={0}
          sx={{ p: 2, borderRadius: '14px', border: '1px solid', borderColor: 'divider' }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(t.color, 0.1),
                color: t.color,
                flexShrink: 0,
                '& svg': { fontSize: 20 },
              }}
            >
              {t.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {t.label}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {loading ? '…' : t.value}
              </Typography>
            </Box>
          </Stack>
          {t.rate != null && !loading && (
            <LinearProgress
              variant="determinate"
              value={Math.min(t.rate, 100)}
              sx={{
                mt: 1.25,
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(t.color, 0.12),
                '& .MuiLinearProgress-bar': { bgcolor: t.color, borderRadius: 3 },
              }}
            />
          )}
        </Paper>
      ))}
    </Box>
  );
}

function DataTable({
  columns,
  rows,
  rowKey,
  loading,
  error,
  emptyText,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
}) {
  // When the API doesn't return a total, allow "next page" while the page is full
  const count =
    total != null ? total : rows.length === rowsPerPage ? -1 : page * rowsPerPage + rows.length;

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px' }}
    >
      {loading ? (
        <Box sx={{ p: 4 }}>
          <LoaderSkeleton />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ borderRadius: '12px' }}>
            {error}
          </Alert>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    fontWeight: 700,
                    color: 'text.secondary',
                    bgcolor: '#fafbfc',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 6, color: 'text.secondary' }}
                  >
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r, i) => (
                  <TableRow key={rowKey(r) || i} hover>
                    {columns.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.align}
                        sx={{ whiteSpace: c.wrap ? 'normal' : 'nowrap' }}
                      >
                        {c.render ? c.render(r) : r[c.key] ?? '–'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      )}
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_, p) => onPageChange(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </TableContainer>
  );
}

/**
 * A Financial report page: title, filters (year, facility, status), summary totals,
 * a paged table and CSV export.
 *
 * fetchRows({ facilityId, status, year, start, stop }) must resolve to the API response
 * ({ result: { data, total? } }); start is the 1-based page number, stop the page size.
 */
export default function FinancialReportPage({
  title,
  description,
  icon,
  listTitle,
  noun,
  columns,
  rowKey,
  statusOptions,
  fetchRows,
  exportName,
  note,
}) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [facility, setFacility] = useState(null);
  const [status, setStatus] = useState('');

  const [facilities, setFacilities] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const facilityId = facility?.facilityId || '';
  const yearParam = year === 'all' ? '' : year;

  useEffect(() => {
    setFacilitiesLoading(true);
    getFacilitiyV2('', '', '', '', '', '', '', '', 1, 1000)
      .then((res) => setFacilities(res?.result?.data || []))
      .catch(() => setFacilities([]))
      .finally(() => setFacilitiesLoading(false));
  }, []);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await getPaymentSummary(yearParam, '', facilityId);
      setSummary(res?.result?.data || null);
    } catch (e) {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [yearParam, facilityId]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchRows({
        facilityId,
        status,
        year: yearParam,
        start: page + 1,
        stop: rowsPerPage,
      });
      setRows(res?.result?.data || []);
      setTotal(res?.result?.total ?? res?.result?.totalCount ?? null);
    } catch (e) {
      setRows([]);
      setTotal(null);
      if (!isEmptyResult(e)) {
        setError(e?.result?.status?.desc || e?.message || `Unable to load ${noun}.`);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchRows, facilityId, status, yearParam, page, rowsPerPage, noun]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    setPage(0);
  }, [facilityId, status, yearParam, rowsPerPage]);

  const handleExport = () =>
    downloadCsv(`iems-${exportName}-${year === 'all' ? 'all-years' : year}.csv`, columns, rows);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header + filters */}
      <Stack spacing={2} sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(BRAND, 0.08),
              color: BRAND,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Year</InputLabel>
            <Select label="Year" value={year} onChange={(e) => setYear(e.target.value)}>
              <MenuItem value="all">All years</MenuItem>
              {getYears()
                .slice()
                .reverse()
                .map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            options={facilities}
            loading={facilitiesLoading}
            value={facility}
            onChange={(_, v) => setFacility(v)}
            getOptionLabel={(o) => o?.facilityName || ''}
            isOptionEqualToValue={(o, v) => o.facilityId === v.facilityId}
            renderInput={(params) => (
              <TextField {...params} label="Facility" placeholder="All facilities" />
            )}
            sx={{ width: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {statusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={() => {
                  loadSummary();
                  loadRows();
                }}
                disabled={loading}
              >
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <SummaryTiles summary={summary} loading={summaryLoading} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {listTitle}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadOutlined />}
          disabled={!rows.length || loading}
          onClick={handleExport}
          sx={{ borderRadius: '8px' }}
        >
          Export CSV
        </Button>
      </Stack>

      {note && year !== 'all' && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {note}
        </Typography>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={rowKey}
        loading={loading}
        error={error}
        emptyText={`No ${noun} found for these filters`}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    </Box>
  );
}
